import type { StreamingChatId, StreamingMessageId } from "@storytell/streaming";
import { type Accessor, createContext, createSignal, type ParentComponent, useContext } from "solid-js";

interface ChatScrollContextValue {
  scrollContainerRef: Accessor<HTMLDivElement | undefined>;
  setScrollContainerRef: (el: HTMLDivElement | undefined) => void;
  chatId: Accessor<StreamingChatId | undefined>;
  setChatId: (id: StreamingChatId | undefined) => void;
  /** When side panel is closed: whether there is room to show the floating timeline (not over chat). Set by ChatThreadView. */
  hasRoomForTimeline: Accessor<boolean>;
  setHasRoomForTimeline: (v: boolean) => void;
  /** When side panel is closed: right inset in px for the floating timeline. Set by ChatThreadView. */
  timelineRightInset: Accessor<number>;
  setTimelineRightInset: (v: number) => void;
  /** Bump to request ChatThreadView to re-measure timeline position (e.g. after side panel close transition). */
  requestTimelineMeasure: Accessor<number>;
  bumpTimelineMeasure: () => void;
  /** True while the user has pointer down on the timeline bar or is dragging the blue dot. Used to show "Show panel" tooltip during interaction. */
  timelinePointerActive: Accessor<boolean>;
  setTimelinePointerActive: (v: boolean) => void;
  /** Read back only while `chatId` is still the chat it was published for; another chat's reads `undefined`. */
  activeMessageId: Accessor<StreamingMessageId | undefined>;
  setActiveMessageId: (chatId: StreamingChatId, id: StreamingMessageId) => void;
  /** Read back only while `chatId` is still the chat it was published for; another chat's reads `[]`. */
  activeMessageIds: Accessor<StreamingMessageId[]>;
  setActiveMessageIds: (chatId: StreamingChatId, ids: StreamingMessageId[]) => void;
}

const ChatScrollContext = createContext<ChatScrollContextValue>();

export function createChatScrollState(): ChatScrollContextValue {
  const [scrollContainerRef, setScrollContainerRef] = createSignal<HTMLDivElement | undefined>();
  const [chatId, setChatId] = createSignal<StreamingChatId | undefined>();
  const [hasRoomForTimeline, setHasRoomForTimeline] = createSignal(false);
  const [timelineRightInset, setTimelineRightInset] = createSignal(40);
  const [requestTimelineMeasure, setRequestTimelineMeasure] = createSignal(0);
  const bumpTimelineMeasure = () => setRequestTimelineMeasure((n) => n + 1);
  const [timelinePointerActive, setTimelinePointerActive] = createSignal(false);
  // Keyed by chat rather than cleared when the chat id changes: a clear runs in the
  // same flush as the new chat's first publish and can land after it.
  const [centerMessage, setCenterMessage] = createSignal<{ chatId: StreamingChatId; id: StreamingMessageId }>();
  const [activePath, setActivePath] = createSignal<{ chatId: StreamingChatId; ids: StreamingMessageId[] }>();

  return {
    scrollContainerRef,
    setScrollContainerRef,
    chatId,
    setChatId,
    hasRoomForTimeline,
    setHasRoomForTimeline,
    timelineRightInset,
    setTimelineRightInset,
    requestTimelineMeasure,
    bumpTimelineMeasure,
    timelinePointerActive,
    setTimelinePointerActive,
    activeMessageId: () => {
      const message = centerMessage();
      return message && message.chatId === chatId() ? message.id : undefined;
    },
    setActiveMessageId: (forChat, id) => setCenterMessage({ chatId: forChat, id }),
    activeMessageIds: () => {
      const path = activePath();
      return path && path.chatId === chatId() ? path.ids : [];
    },
    setActiveMessageIds: (forChat, ids) => setActivePath({ chatId: forChat, ids }),
  };
}

export const ChatScrollProvider: ParentComponent = (props) => {
  const value = createChatScrollState();
  return <ChatScrollContext.Provider value={value}>{props.children}</ChatScrollContext.Provider>;
};

export const useChatScrollContext = (): ChatScrollContextValue => {
  const ctx = useContext(ChatScrollContext);
  if (!ctx) throw new Error("useChatScrollContext must be used within ChatScrollProvider");
  return ctx;
};

export const useChatScrollContextSafe = (): ChatScrollContextValue | undefined => useContext(ChatScrollContext);
